import {
  GENERAL_CONCEPTUAL_UNDERSTANDING,
  WORK_IS_COMPLETE_AND_CORRECT
} from 'pages/Teachers/Insights/constants';

export const getStatusColor = ({ progress, targetAccuracy }) => {
  if (progress < targetAccuracy / 2) return 'pink';
  if (progress < targetAccuracy) return 'yellow';
  return 'green';
};

export const getProgressColorClass = ({ color, styleModule }) => {
  const colorClasses = {
    green: styleModule.fillGreen,
    yellow: styleModule.fillYellow,
    pink: styleModule.fillPink
  };
  return colorClasses[color] || styleModule.fillGreen;
};

export const formatDateString = ({ isoString }) => {
  if (!isoString || typeof isoString !== 'string') {
    return '';
  }
  const dateParts = isoString.split('T')[0].split('-');
  if (dateParts.length !== 3 || dateParts.some((part) => isNaN(Number(part)))) {
    return isoString;
  }
  const [year, month, day] = dateParts;
  return `${day}/${month}/${year}`;
};

export const getStrokeColor = ({ masteryLevel }) => {
  if (masteryLevel === WORK_IS_COMPLETE_AND_CORRECT || masteryLevel === 83.3)
    return '#4A8640';
  if (masteryLevel === GENERAL_CONCEPTUAL_UNDERSTANDING || masteryLevel === 50)
    return '#C0871E';
  return '#CC2762';
};

export const getDotColorClass = ({ masteryLevel, styleModule }) => {
  if (masteryLevel === GENERAL_CONCEPTUAL_UNDERSTANDING)
    return styleModule.dotOrange;
  if (masteryLevel === WORK_IS_COMPLETE_AND_CORRECT)
    return styleModule.dotGreen;
  return styleModule.dotPink;
};

export const calculateGraphPoints = ({ records, styleModule }) => {
  const rawRecords = Array.isArray(records)
    ? records
    : records?.records || records?.data || [];

  if (rawRecords.length === 0) return [];

  const sortedRecords = [...rawRecords].sort(
    (a, b) => new Date(a.created_at) - new Date(b.created_at)
  );

  const minTime = new Date(sortedRecords[0].created_at).getTime();
  const maxTime = new Date(
    sortedRecords[sortedRecords.length - 1].created_at
  ).getTime();
  const timeSpan = maxTime - minTime;

  const getStatusLabel = (mastery) => {
    if (mastery === WORK_IS_COMPLETE_AND_CORRECT) return 'Celebrate';
    if (mastery === GENERAL_CONCEPTUAL_UNDERSTANDING) return 'Support';
    return 'Intervene';
  };

  const getNumericYValue = (mastery) => {
    const normalized = String(mastery).toLowerCase();
    if (
      normalized.includes('complete') ||
      normalized.includes('correct') ||
      normalized.includes('celebrate')
    )
      return 83.3;
    if (
      normalized.includes('conceptual') ||
      normalized.includes('understanding') ||
      normalized.includes('support')
    )
      return 50.0;
    return 16.6;
  };

  let points = sortedRecords.map((record) => {
    const currentTime = new Date(record.created_at).getTime();
    const timeRatio = timeSpan > 0 ? (currentTime - minTime) / timeSpan : 0.5;

    const initialX = 12 + timeRatio * 76;
    const rawMastery = record?.mastery_level;

    return {
      x: initialX,
      y: getNumericYValue(rawMastery),
      color: getDotColorClass({ masteryLevel: rawMastery, styleModule }),
      dateLabel: formatDateString({ isoString: record?.created_at }),
      statusLabel: getStatusLabel(rawMastery)
    };
  });

  const MIN_HORIZONTAL_GAP = 6;

  for (let i = 1; i < points.length; i++) {
    if (points[i].x < points[i - 1].x + MIN_HORIZONTAL_GAP) {
      points[i].x = points[i - 1].x + MIN_HORIZONTAL_GAP;
    }
  }

  if (points[points.length - 1].x > 88) {
    points[points.length - 1].x = 88;
    for (let i = points.length - 2; i >= 0; i--) {
      if (points[i].x > points[i + 1].x - MIN_HORIZONTAL_GAP) {
        points[i].x = points[i + 1].x - MIN_HORIZONTAL_GAP;
      }
    }
  }

  return points;
};

export const computeSvgLines = ({ graphPoints }) => {
  const safePoints = Array.isArray(graphPoints) ? graphPoints : [];

  return safePoints.slice(1).map((point, index) => {
    const previousPoint = safePoints[index];
    const strokeColor = getStrokeColor({ masteryLevel: point.y });

    return {
      id: `line-${index}`,
      x1: previousPoint.x,
      y1: 100 - previousPoint.y,
      x2: point.x,
      y2: 100 - point.y,
      stroke: strokeColor
    };
  });
};
